import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaLink } from '@/components/generals/cta-link'
import { HeroTitle } from '@/components/generals/hero-title'
import { Mark } from '@/components/generals/mark'
import { SectionLabel } from '@/components/generals/section-label'
import ContactForm from '@/components/sections/contact-form'

import { REVALIDATE_SECONDS, getServices } from '@/lib/content'
import { CONTACT, SOCIALS } from '@/constants/site'
import { FOOTER_SERVICES } from '@/constants/navigation'

interface ContactPageProps {
    serviceOptions: string[]
}

const NEXT_STEPS = [
    { title: "We read your brief", body: "A senior strategist reviews every inquiry personally." },
    { title: "Introductory call", body: "A 30 minute conversation about goals, audience and timing." },
    { title: "A clear proposal", body: "Scope, timeline, budget and the metrics we will commit to." }
]

const CARD_CORNERS = [
    'left-0 top-0 border-l-2 border-t-2',
    'right-0 top-0 border-r-2 border-t-2',
    'bottom-0 left-0 border-b-2 border-l-2',
    'bottom-0 right-0 border-b-2 border-r-2'
]

const ContactPage: FC<ContactPageProps> = ({ serviceOptions }): ReactNode => {
    const socials = SOCIALS.filter((social) => social.url)
    const details = [
        CONTACT.email ? { label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}` } : null,
        CONTACT.phone ? { label: 'Phone', value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^\d+]/g, '')}` } : null,
        CONTACT.location ? { label: 'Location', value: CONTACT.location, href: '' } : null
    ].filter((item): item is { label: string, value: string, href: string } => Boolean(item))

    return (
        <Layout>
            <Seo title='Contact' description='Start a project with BLITZ. Tell us what you are building and where you want to go, and we will reply within one business day.' />

            <section id='contact-hero' aria-labelledby='contact-hero-title' data-nav-theme='dark' className='relative overflow-hidden bg-charcoal text-cream'>
                <div aria-hidden='true' className='reveal-fade pointer-events-none absolute -right-40 top-20 size-[26rem] rounded-full border border-cream/10 md:size-[36rem]' style={revealDelay(400)} />

                <div className='relative mx-auto max-w-[1440px] px-5 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40'>
                    <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-5'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-cream/15' style={revealDelay(100)} />
                        <SectionLabel tone='dark' className='reveal-fade'>Contact</SectionLabel>
                        <p className='reveal-fade font-mono text-[11px] uppercase tracking-[0.2em] text-cream/65' style={revealDelay(150)}>
                            Reply within one business day
                        </p>
                    </div>

                    <div className='mt-10 grid gap-14 md:mt-14 lg:grid-cols-12 lg:items-end lg:gap-10'>
                        <div className='lg:col-span-7'>
                            <HeroTitle
                                id='contact-hero-title'
                                className='type-hero'
                                delay={200}
                                lines={[
                                    <>Let&apos;s make something</>,
                                    <Mark key='noticing' tone='dark'>worth noticing.</Mark>
                                ]}
                            />
                            <p className='reveal-fade mt-7 max-w-lg text-[17px] leading-relaxed text-cream/80' style={revealDelay(420)}>
                                Tell us what you&apos;re building, where you want to go, and what you want to achieve. We reply to every inquiry within one business day.
                            </p>
                            <div className='reveal-fade mt-9 flex flex-wrap items-center gap-x-8 gap-y-5' style={revealDelay(520)}>
                                <CtaLink href='#inquiry' variant='primary-dark'>Start your inquiry</CtaLink>
                                {CONTACT.email && (
                                    <CtaLink href={`mailto:${CONTACT.email}`} variant='text-light' icon='up-right'>Email us</CtaLink>
                                )}
                            </div>
                        </div>

                        {details.length > 0 && (
                            <aside aria-labelledby='direct-line-title' className='reveal-clip relative bg-cream p-6 text-charcoal sm:p-8 lg:col-span-4 lg:col-start-9' style={revealDelay(380)}>
                                {CARD_CORNERS.map((corner) => (
                                    <span key={corner} aria-hidden='true' className={`absolute size-3 border-coral ${corner}`} />
                                ))}
                                <p id='direct-line-title' className='flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/80'>
                                    Direct line
                                    <span aria-hidden='true' className='size-2 bg-coral' />
                                </p>
                                <dl className='mt-6 space-y-5'>
                                    {details.map((detail, index) => (
                                        <div key={detail.label} className='reveal-fade border-t border-charcoal/25 pt-3' style={revealDelay(620 + index * 80)}>
                                            <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>{detail.label}</dt>
                                            <dd className='mt-1.5 break-words text-[15px] font-semibold'>
                                                {detail.href ? (
                                                    <a href={detail.href} className='underline-offset-4 hover:underline'>{detail.value}</a>
                                                ) : detail.value}
                                            </dd>
                                        </div>
                                    ))}
                                    <div className='reveal-fade border-t border-charcoal/25 pt-3' style={revealDelay(620 + details.length * 80)}>
                                        <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>First step</dt>
                                        <dd className='mt-1.5 text-[15px] font-semibold'>A 30 minute introductory call</dd>
                                    </div>
                                </dl>
                            </aside>
                        )}
                    </div>
                </div>
            </section>

            <section id='inquiry' aria-labelledby='inquiry-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                <div className='mx-auto grid max-w-[1440px] gap-16 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                    <div className='space-y-14 lg:col-span-4'>
                        {socials.length > 0 && (
                            <div>
                                <SectionLabel>Social</SectionLabel>
                                <ul className='mt-6 flex flex-wrap gap-3'>
                                    {socials.map((social) => (
                                        <li key={social.label}>
                                            <a
                                                href={social.url}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center gap-2 rounded-sm border border-charcoal/30 px-4 py-2 text-[13px] font-semibold transition-colors hover:border-charcoal hover:bg-charcoal hover:text-cream'
                                            >
                                                {social.label}
                                                <ArrowUpRight aria-hidden='true' className='size-4' />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div>
                            <SectionLabel>What happens next</SectionLabel>
                            <ol className='mt-6 border-t border-charcoal'>
                                {NEXT_STEPS.map((step, index) => (
                                    <li key={step.title} className='grid grid-cols-[2.5rem_1fr] gap-3 border-b border-charcoal/25 py-5'>
                                        <span className='font-mono text-xs text-charcoal/75'>{pad(index + 1)}</span>
                                        <div>
                                            <p className='font-heading font-wide text-[15px] font-black uppercase'>{step.title}</p>
                                            <p className='mt-1 text-[14px] text-charcoal/80'>{step.body}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <figure className='relative hidden lg:block'>
                            <div aria-hidden='true' className='absolute -bottom-4 -right-4 h-1/2 w-1/2 bg-coral' />
                            <div data-reveal-image className='relative aspect-[4/5] overflow-hidden bg-stone'>
                                <Image src='/images/contact/architecture.jpg' alt='Sunlit staircase casting geometric shadows on cream walls' fill sizes='30vw' className='object-cover' />
                            </div>
                        </figure>
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        <div className='relative border-t-4 border-charcoal bg-stone/60 p-6 sm:p-10 md:p-12'>
                            <h2 id='inquiry-title' className='font-heading text-[clamp(1.75rem,2.6vw,2.4rem)] font-extrabold leading-[1.02] tracking-[-0.035em]'>
                                Project inquiry
                            </h2>
                            <p className='mt-3 max-w-md text-[15px] leading-relaxed text-charcoal/85'>
                                A few details help us prepare. Fields marked with an asterisk are required.
                            </p>
                            <div className='mt-10'>
                                <ContactForm serviceOptions={serviceOptions} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactPageProps> = async () => {
    const services = await getServices()
    const serviceOptions = services.length
        ? services.map((service) => service.shortTitle)
        : FOOTER_SERVICES.map((service) => service.label)

    return {
        props: { serviceOptions },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ContactPage
